import { useEffect, useRef, useState } from 'react';

import { ReactComponent as IconCheck } from 'asserts/icons/ic_chevron_check.svg';
import { ReactComponent as IconArrow } from 'asserts/icons/ic_nav_arrow_up.svg';
import styled, { keyframes } from 'styled-components';
import { color } from 'asserts/styles/colors';

interface SelectBoxProps {
  isMonth?: boolean;
  label: number | string;
  options: number[];
  selectedValue: number;
  onChange: (value: number) => void;
}

const Select = ({
  label,
  options,
  selectedValue,
  onChange,
  isMonth
}: SelectBoxProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const selectRef = useRef<HTMLUListElement>(null);

  const handleClickSelectBox = () => {
    setShowOptions((prev) => !prev);
  };

  const handleClickOption = (value: number) => {
    setShowOptions(false);
    onChange(value);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const targetNode = e.target as Node | null;
      if ((targetNode as Element).tagName === 'P') {
        return;
      }

      if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
        setShowOptions(false);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [selectRef]);

  return (
    <SelectBox>
      <Label onClick={handleClickSelectBox}>
        <p>{label}</p>
        {showOptions ? (
          <AnimationDown>
            <IconArrow />
          </AnimationDown>
        ) : (
          <AnimationUp>
            <IconArrow />
          </AnimationUp>
        )}
      </Label>
      <SelectOptions $show={showOptions} ref={selectRef}>
        {options.map((option) => (
          <Option
            key={option}
            $isSelected={option === selectedValue}
            onClick={() => handleClickOption(option)}
          >
            {isMonth ? (option === 0 ? '전체' : `${option}월`) : `${option}년`}
            {option === selectedValue && <IconCheck className="check-icon" />}
          </Option>
        ))}
      </SelectOptions>
    </SelectBox>
  );
};

export default Select;

const SelectBox = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  font-size: 13px;
  line-height: 16px;
  min-width: 120px;
  cursor: pointer;
  text-align: left;
  border: 1px solid ${color.white01};
  border-radius: 4px;

  & p {
    width: 100%;
  }
`;
const SelectOptions = styled.ul<{ $show: boolean }>`
  position: absolute;
  display: ${(props) => (props.$show ? 'block' : 'none')};
  list-style: none;
  top: 30px;
  left: 0;
  width: 100%;
  overflow: hidden;
  padding: 4px;
  border-radius: 8px;
  background-color: #fff;
  color: #000;
  text-align: left;
`;

const Option = styled.li<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;

  transition: background-color 0.25s ease-in-out;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  ${({ $isSelected }) =>
    $isSelected &&
    `
    & .check-icon {
      margin-left: auto;
    }
  `}
`;

const ArrowUp = keyframes`
  0% {
    transform:rotate(0deg);
  }
  100%{
    transform:rotate(180deg);
  }
`;

const ArrowDown = keyframes`
  0% {
    transform:rotate(180deg);
  }
  100%{
    transform:rotate(0deg);
  }
`;

const AnimationDown = styled.span`
  animation: ${ArrowDown} 0.3s linear forwards;
`;

const AnimationUp = styled.span`
  animation: ${ArrowUp} 0.3s linear forwards;
`;
